import ShuffleIcon from "@mui/icons-material/Shuffle";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { selectKnownOutfits } from "../../app/slices/MetadataSlice";
import { selectSelectedActor, updateActor } from "../../app/slices/TrainingSlice";
import PiSegmentedToggle from "../../components/PiSegmentedToggle";
import { initialPreviewGender, outfitItem } from "../outfit_picker/outfitPickerModel";
import OutfitPickerDialog from "../outfit_picker/OutfitPickerDialog";
import OutfitStage from "../outfit_picker/OutfitStage";

enum OutfitMode {
  manual = "manual",
  random = "random",
}

/**
 * The preset's outfit row: Manual / Random (like the in-game name), then one card
 * with the outfit's render, its name and "Change outfit". An empty outfit is Random.
 * Choosing Manual from Random opens the picker; until an outfit is picked the
 * preset stays Random.
 */
export default function ActorOutfitSelector() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const selectedActor = useSelector(selectSelectedActor);
  const knownOutfits = useSelector(selectKnownOutfits);
  const [pickerOpen, setPickerOpen] = useState(false);
  if (!selectedActor) return null;

  const value = selectedActor.outfit ?? "";
  const mode = value ? OutfitMode.manual : OutfitMode.random;
  const item = value ? outfitItem(knownOutfits[value] ?? { name: value }, "field") : undefined;

  const setOutfit = (outfit: string) => dispatch(updateActor({ ...selectedActor, outfit }));

  const onChangeMode = (next: OutfitMode) => {
    if (next === mode) return;
    if (next === OutfitMode.random) setOutfit("");
    else setPickerOpen(true);
  };

  return (
    <div className="actorpreseteditor-outfit" data-testid="outfit-picker-field">
      <PiSegmentedToggle
        ariaLabel="outfit-mode-toggle"
        className="actorpreseteditor-segment actorpreseteditor-control"
        variant="square"
        fullWidth
        layoutId="actorpreseteditor-outfit-mode-toggle"
        options={[
          { testId: "outfit-mode-manual", label: String(t("actor_preset_editor.manual")), value: OutfitMode.manual },
          { testId: "outfit-mode-random", label: String(t("actor_preset_editor.random")), value: OutfitMode.random },
        ]}
        value={mode}
        onChange={onChangeMode}
      />
      {/* one card at the control width: the render, then its name and Change in a bar
          (Design Studio actor-preset-rhythm/a2-outfit-card); Random has no bar, the toggle says it */}
      {mode === OutfitMode.manual ? (
        <div className="actorpreseteditor-outfit-card">
          <button
            type="button"
            className="actorpreseteditor-outfit-stage"
            aria-label={String(t("outfit_picker.change_outfit"))}
            onClick={() => setPickerOpen(true)}
            data-testid="outfit-stage-preview"
          >
            {item && <OutfitStage item={item} gender={initialPreviewGender(selectedActor.gender)} fill />}
          </button>
          <div className="actorpreseteditor-outfit-bar">
            <span className="actorpreseteditor-outfit-name" data-testid="outfit-name">
              {item?.kind === "outfit" ? item.outfit.name : value}
            </span>
            <button
              type="button"
              className="actorpreseteditor-button"
              onClick={() => setPickerOpen(true)}
              data-testid="outfit-stage-change"
            >
              <SwapHorizIcon fontSize="inherit" className="actorpreseteditor-button-icon" />
              {String(t("outfit_picker.change_outfit"))}
            </button>
          </div>
        </div>
      ) : (
        <div className="actorpreseteditor-outfit-random" role="status" data-testid="outfit-random-note">
          <ShuffleIcon aria-hidden="true" className="actorpreseteditor-outfit-random-icon" />
          <span className="actorpreseteditor-outfit-random-title">{String(t("outfit_picker.random_outfit"))}</span>
          <span>{String(t("actor_preset_editor.outfit_random_note"))}</span>
        </div>
      )}
      <OutfitPickerDialog
        open={pickerOpen}
        context="preset"
        actor={selectedActor}
        value={value}
        onChoose={setOutfit}
        onClose={() => setPickerOpen(false)}
      />
    </div>
  );
}
